/**
 * Settings as a grid of connectors.
 *
 * Every integration used to have its configuration panel open on the page at
 * once, so the screen led with credentials, sender mailboxes and price IDs
 * whether or not anyone was changing them. Two problems with that: the thing an
 * administrator usually wants to know — is this connected? — was buried inside
 * the thing they rarely want to touch, and sensitive configuration was on screen
 * by default, in a workspace that is often being shared or screen-shared.
 *
 * So: a tile per service showing its logo and whether it is live, and the
 * configuration revealed only when that connector is switched on. A connector
 * that is already connected opens enabled, because hiding the settings for
 * something that is running would be hiding the thing you came to check.
 *
 * No secret is rendered here or by any panel below it. The status queries return
 * booleans and masked hints; the values themselves stay on the server.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, CircleAlert, Plug, Settings2, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import GoogleWorkspacePanel from "@/components/GoogleWorkspacePanel";
import MembershipPricePanel from "@/components/MembershipPricePanel";
import { GoogleWorkspaceLogo, StripeLogo, ZoomLogo } from "@/components/ConnectorLogos";
import EmailNotificationCatalog from "@/components/EmailNotificationCatalog";

/** What the server reports about Zoom — booleans and masked hints, never values. */
type ZoomIntegrationStatus = {
  configured: boolean;
  accountConfigured: boolean;
  clientConfigured: boolean;
  hostConfigured: boolean;
  accountHint: string | null;
  hostHint: string | null;
};

type ConnectorState = {
  key: string;
  name: string;
  purpose: string;
  logo: ReactNode;
  /** Live and usable right now. */
  connected: boolean;
  /** Partly set up — enough to show, not enough to work. */
  attention?: string | null;
  /** Safe, non-secret facts to show on the tile once connected. */
  facts: Array<{ label: string; value: string }>;
  panel: ReactNode;
};

function StatusChip({ connected, attention }: { connected: boolean; attention?: string | null }) {
  if (connected) {
    return (
      <span className="connector-chip" data-tone="on">
        <Check size={12} aria-hidden="true" /> Connected
      </span>
    );
  }
  if (attention) {
    return (
      <span className="connector-chip" data-tone="warn">
        <CircleAlert size={12} aria-hidden="true" /> Needs attention
      </span>
    );
  }
  return <span className="connector-chip" data-tone="off">Not connected</span>;
}

/**
 * One connector tile.
 *
 * `enabled` is not a prop any more, and that is the point: a tile reads its own
 * switch off `connector.connected`, which is the server's confirmation that the two
 * systems actually talk to each other. There is no local flag that can hold the
 * switch on, so a connector cannot look enabled because someone clicked it.
 */
function ConnectorCard({
  connector,
  onConfigure,
}: {
  connector: ConnectorState;
  onConfigure: () => void;
}) {
  /* The single source of truth for this tile. Confirmed by the server, or off. */
  const enabled = connector.connected;

  return (
    <section className="connector-card" data-enabled={enabled || undefined}>
      <div className="connector-card__head">
        <span className="connector-card__logo" aria-hidden="true">{connector.logo}</span>
        <div className="connector-card__identity">
          <h3>{connector.name}</h3>
          <p>{connector.purpose}</p>
        </div>
        <StatusChip connected={connector.connected} attention={connector.attention} />
      </div>

      {/*
        The quick read: what this connector is actually pointing at, without
        opening anything. Absent rather than empty when there is nothing to
        report, so an unconfigured tile does not show a row of dashes.
      */}
      {connector.facts.length > 0 && (
        <dl className="connector-card__facts">
          {connector.facts.map(fact => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd title={fact.value}>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="connector-card__controls">
        {/*
          The switch reports a verified connection; it does not set one. Clicking it
          opens the configuration, because connecting is something the other system
          has to confirm and no amount of clicking here can substitute for that.

          It stays a switch rather than becoming a second button so the tile can still
          be read at a glance — on or off is the question an administrator arrives
          with. `aria-checked` therefore only ever reflects the server's answer.
        */}
        <button
          type="button"
          className="connector-card__toggle"
          role="switch"
          aria-checked={enabled}
          aria-label={`${connector.name} connector`}
          title={enabled
            ? `${connector.name} is connected. Open it to change or disconnect it.`
            : `${connector.name} is not connected yet. Open it to set up the connection.`}
          onClick={onConfigure}
        >
          <span className="connector-card__switch" aria-hidden="true" data-on={enabled || undefined}>
            <span />
          </span>
          {enabled ? "Enabled" : "Disabled"}
        </button>

        {/*
          Never disabled. It used to be gated on the toggle, which made a brand-new
          connector impossible to reach: it was off, so the only control that could
          configure it was unavailable, and switching it on faked being connected
          instead of connecting.
        */}
        <button
          type="button"
          className="connector-card__configure"
          onClick={onConfigure}
        >
          <Settings2 size={13} aria-hidden="true" />
          {connector.connected ? "View connection" : "Set up connection"}
        </button>
      </div>
    </section>
  );
}

/**
 * The connector's full configuration, in a modal.
 *
 * A modal rather than an inline expansion: expanding a tile in place pushed the
 * grid around and made the other connectors jump or disappear below the fold,
 * so checking one meant losing sight of the rest. The grid stays exactly where
 * it is; the configuration opens over it and closes back to the same view.
 */
function ConnectorModal({ connector, onClose }: { connector: ConnectorState; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="connector-modal" onClick={onClose}>
      <div
        className="connector-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connector-modal-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="connector-modal__head">
          <span className="connector-card__logo" aria-hidden="true">{connector.logo}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="connector-modal-title">{connector.name}</h2>
            <p>{connector.purpose}</p>
          </div>
          <StatusChip connected={connector.connected} attention={connector.attention} />
          <button ref={closeRef} type="button" className="connector-modal__close" onClick={onClose} aria-label={`Close ${connector.name} settings`}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="connector-modal__body">{connector.panel}</div>

        <footer className="connector-modal__foot">
          <button type="button" className="crm-drawer__secondary" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Zoom is configured from the server environment rather than through a browser
 * OAuth handshake, so this panel reports state and says where the values live
 * instead of offering fields that would write nothing.
 */
function ZoomConnectorPanel({ status }: { status: ZoomIntegrationStatus | undefined }) {
  /*
   * Presence is not correctness. A revoked app, a rotated secret and a typo all
   * look identical to a "the variable is set" check, so the panel offers the
   * real exchange and reports what Zoom said.
   */
  const test = trpc.events.zoomConnectionTest.useMutation();

  const rows: Array<{ label: string; ok: boolean; detail: string }> = [
    { label: "Account", ok: Boolean(status?.accountConfigured), detail: status?.accountHint ?? "ZOOM_ACCOUNT_ID is not set" },
    { label: "App credentials", ok: Boolean(status?.clientConfigured), detail: status?.clientConfigured ? "Client ID and secret are set" : "ZOOM_CLIENT_ID and ZOOM_CLIENT_SECRET are not set" },
    { label: "Meeting host", ok: Boolean(status?.hostConfigured), detail: status?.hostHint ?? "ZOOM_HOST_USER_ID is not set" },
  ];

  return (
    <div className="connector-panel">
      <p className="connector-panel__lede">
        Zoom uses a server-to-server OAuth app, so its credentials are held in the server environment
        and never in the browser. This panel reports what the server can see.
      </p>
      <ul className="connector-panel__checks">
        {rows.map(row => (
          <li key={row.label} data-ok={row.ok || undefined}>
            <strong>{row.label}</strong>
            <span>{row.detail}</span>
          </li>
        ))}
      </ul>
      {!status?.configured && (
        <p className="connector-panel__note">
          Until all three are set, linking a Zoom meeting to an event will fail. Set them in the
          server environment and restart, then reload this page.
        </p>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap", marginTop: ".9rem" }}>
        <button
          type="button"
          className="crm-drawer__secondary"
          disabled={!status?.configured || test.isPending}
          onClick={() => test.mutate()}
          style={{ minHeight: 38 }}
        >
          {test.isPending ? "Testing…" : "Test connection"}
        </button>
        {test.data && (
          <span role="status" style={{ color: test.data.ok ? "#BBF7D0" : "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {test.data.message}
          </span>
        )}
        {test.error && (
          <span role="alert" style={{ color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {test.error.message}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ConnectorSettings() {
  const workspace = trpc.googleWorkspace.status.useQuery(undefined, { retry: false });
  const stripeHealth = trpc.members.membershipPriceHealth.useQuery(undefined, { retry: false });
  const zoomQuery = trpc.events.zoomIntegrationStatus.useQuery(undefined, { retry: false });

  /*
   * There is deliberately no local enablement state.
   *
   * A per-session override map used to decide each switch, falling back to the
   * server's `connected`. That let a connector read "Enabled" the moment someone
   * clicked it, before — or without — the other system ever confirming anything,
   * and the tile then contradicted its own status chip. Enablement is now read
   * straight off each connector's status query, so the only way to show enabled is
   * to actually be connected.
   *
   * The single piece of state left is which connector is open, which is about the
   * modal and not about whether anything is configured.
   */
  const [openConnector, setOpenConnector] = useState<string | null>(null);
  const zoom = { data: zoomQuery.data as ZoomIntegrationStatus | undefined };

  const connectors: ConnectorState[] = [
    {
      key: "google-workspace",
      name: "Google Workspace",
      purpose: "Sends every email the site produces, from your own mailbox.",
      logo: <GoogleWorkspaceLogo />,
      connected: Boolean(workspace.data?.connected),
      attention: workspace.data && !workspace.data.connected && workspace.data.connectorConfigured
        ? "Connection needs attention"
        : null,
      facts: workspace.data?.senderEmail ? [{ label: "Sending as", value: workspace.data.senderEmail }] : [],
      panel: <GoogleWorkspacePanel />,
    },
    {
      key: "stripe",
      name: "Stripe",
      purpose: "Takes membership and ticket payments.",
      logo: <StripeLogo />,
      connected: Boolean(stripeHealth.data?.configured && stripeHealth.data?.usable),
      attention: stripeHealth.data?.configured && !stripeHealth.data.usable
        ? stripeHealth.data.problems[0] ?? "The configured price cannot be used"
        : null,
      facts: [
        ...(stripeHealth.data?.priceId ? [{ label: "Membership price", value: stripeHealth.data.priceId }] : []),
        ...(stripeHealth.data?.liveMode === null || stripeHealth.data?.liveMode === undefined
          ? []
          : [{ label: "Mode", value: stripeHealth.data.liveMode ? "Live" : "Test" }]),
      ],
      panel: <MembershipPricePanel />,
    },
    {
      key: "zoom",
      name: "Zoom",
      purpose: "Creates meetings and join links for virtual events.",
      logo: <ZoomLogo />,
      connected: Boolean(zoom.data?.configured),
      attention: zoom.data && !zoom.data.configured && (zoom.data.accountConfigured || zoom.data.clientConfigured)
        ? "Partly configured"
        : null,
      facts: [
        ...(zoom.data?.accountHint ? [{ label: "Account", value: zoom.data.accountHint }] : []),
        ...(zoom.data?.hostHint ? [{ label: "Host", value: zoom.data.hostHint }] : []),
      ],
      panel: <ZoomConnectorPanel status={zoom.data} />,
    },
  ];

  const connectedCount = connectors.filter(connector => connector.connected).length;
  const active = connectors.find(connector => connector.key === openConnector) ?? null;

  return (
    <div className="connector-settings">
      <header className="connector-settings__head">
        <div>
          <p className="connector-settings__eyebrow"><Plug size={13} aria-hidden="true" /> Connectors</p>
          <h1>Settings</h1>
          <p className="connector-settings__lede">
            {connectedCount} of {connectors.length} services connected. A connector reads Enabled only
            once the other service confirms the connection — open one to set it up or change what it
            points at. Credentials themselves are never shown here.
          </p>
        </div>
      </header>

      <div className="connector-settings__grid">
        {connectors.map(connector => (
          <ConnectorCard
            key={connector.key}
            connector={connector}
            onConfigure={() => setOpenConnector(connector.key)}
          />
        ))}
      </div>

      <EmailNotificationCatalog />

      {active && <ConnectorModal connector={active} onClose={() => setOpenConnector(null)} />}

      <style>{`
        .connector-settings { padding: clamp(1.25rem, 3vw, 2.5rem); }

        .connector-settings__head { margin-bottom: 1.5rem; }

        .connector-settings__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: .4rem;
          margin: 0 0 .35rem;
          color: #F9A8D4;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 750;
          letter-spacing: .12em;
          text-transform: uppercase;
        }

        .connector-settings__head h1 {
          margin: 0;
          color: #fff;
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(2rem, 4vw, 3rem);
          letter-spacing: .025em;
          line-height: 1;
        }

        .connector-settings__lede {
          max-width: 62ch;
          margin: .6rem 0 0;
          color: #8E9AAF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.6;
        }

        /*
          One column per connector until the panels need the room. A revealed
          panel spans the grid so a form is never squeezed into a third of the
          workspace.
        */
        .connector-settings__grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
          gap: 1rem;
          /* Stretch, not start: the tiles are a uniform grid now that their
             configuration opens in a modal rather than in place. */
          align-items: stretch;
        }

        .connector-card {
          display: flex;
          /*
            One height for every tile. Letting the grid size each tile to its own
            content meant a connector with two configured facts stood taller than
            its neighbours and the row read as ragged. The min-height sets the
            floor; height 100% makes any tile that does grow take the row with it.

            Note for future edits: this is a template literal, so a backtick in a
            CSS comment ends the string and breaks the file.
          */
          min-height: 15.5rem;
          height: 100%;
          flex-direction: column;
          gap: .85rem;
          padding: 1.15rem;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 1rem;
          background: linear-gradient(150deg, rgba(24,31,58,.9), rgba(9,13,29,.94));
          transition: border-color .16s ease;
        }

        .connector-card[data-enabled] { border-color: rgba(0,212,255,.28); }

        .connector-card__head {
          display: flex;
          align-items: flex-start;
          gap: .8rem;
        }

        .connector-card__logo {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 2.75rem;
          height: 2.75rem;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: .7rem;
          background: rgba(255,255,255,.05);
        }

        .connector-card__identity { flex: 1; min-width: 0; }

        .connector-card__identity h3 {
          margin: 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1rem;
          font-weight: 700;
        }

        .connector-card__identity p {
          margin: .2rem 0 0;
          color: #8E9AAF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.5;
        }

        .connector-chip {
          display: inline-flex;
          align-items: center;
          gap: .3rem;
          flex-shrink: 0;
          padding: .28rem .6rem;
          border-radius: 999px;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
          white-space: nowrap;
        }

        .connector-chip[data-tone="on"] { border: 1px solid rgba(134,239,172,.32); background: rgba(134,239,172,.1); color: #BBF7D0; }
        .connector-chip[data-tone="warn"] { border: 1px solid rgba(245,166,35,.34); background: rgba(245,166,35,.1); color: #FCD34D; }
        .connector-chip[data-tone="off"] { border: 1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.04); color: #94A3B8; }

        .connector-card__facts {
          display: grid;
          align-content: start;
          gap: .35rem;
          margin: 0;
          padding: .7rem .8rem;
          border: 1px solid rgba(255,255,255,.07);
          border-radius: .7rem;
          background: rgba(0,0,0,.16);
        }

        .connector-card__facts > div { display: flex; justify-content: space-between; gap: .75rem; }

        .connector-card__facts dt {
          color: #657289;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
        }

        .connector-card__facts dd {
          margin: 0;
          overflow: hidden;
          color: #CFD8E6;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .connector-card__controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: .75rem;
          flex-wrap: wrap;
          /* Pushed to the bottom of the tile regardless of how much sits above. */
          margin-top: auto;
          padding-top: .25rem;
        }

        .connector-card__configure {
          display: inline-flex;
          align-items: center;
          gap: .35rem;
          min-height: 34px;
          padding: 0 .7rem;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: .5rem;
          background: rgba(255,255,255,.05);
          color: #CBD5E1;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
          cursor: pointer;
        }

        .connector-card__configure:hover:not(:disabled) {
          border-color: rgba(0,212,255,.4);
          background: rgba(0,212,255,.1);
          color: #7DEAFF;
        }

        .connector-card__configure:disabled { opacity: .4; cursor: not-allowed; }

        .connector-modal {
          position: fixed;
          inset: 0;
          z-index: 140;
          display: grid;
          place-items: center;
          padding: clamp(1rem, 4vw, 2.5rem);
          background: rgba(4,7,16,.8);
          backdrop-filter: blur(6px);
        }

        .connector-modal__panel {
          display: flex;
          flex-direction: column;
          width: min(46rem, 100%);
          max-height: 88vh;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 1.1rem;
          background:
            radial-gradient(circle at 88% 4%, rgba(0,212,255,.12), transparent 34%),
            linear-gradient(150deg, rgba(24,31,58,.98), rgba(9,13,29,.99));
          box-shadow: 0 30px 90px rgba(0,0,0,.6);
        }

        .connector-modal__head {
          display: flex;
          align-items: flex-start;
          gap: .8rem;
          flex-shrink: 0;
          padding: 1.15rem 1.25rem;
          border-bottom: 1px solid rgba(255,255,255,.08);
        }

        .connector-modal__head h2 {
          margin: 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
        }

        .connector-modal__head p {
          margin: .2rem 0 0;
          color: #8E9AAF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .connector-modal__close {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 1.9rem;
          height: 1.9rem;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .55rem;
          background: rgba(255,255,255,.04);
          color: #94A3B8;
          cursor: pointer;
        }

        .connector-modal__close:hover { color: #fff; border-color: rgba(255,255,255,.24); }

        /* Only the body scrolls, so the title and Done stay put on a long panel. */
        .connector-modal__body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 1.15rem 1.25rem;
        }

        .connector-modal__foot {
          display: flex;
          justify-content: flex-end;
          flex-shrink: 0;
          padding: .9rem 1.25rem;
          border-top: 1px solid rgba(255,255,255,.08);
        }

        .connector-card__toggle {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          padding: 0;
          border: 0;
          background: none;
          color: #CBD5E1;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
          cursor: pointer;
        }

        .connector-card__switch {
          display: inline-flex;
          align-items: center;
          width: 2.4rem;
          height: 1.35rem;
          padding: .18rem;
          border-radius: 999px;
          background: rgba(148,163,184,.28);
          transition: background .16s ease;
        }

        .connector-card__switch > span {
          width: .95rem;
          height: .95rem;
          border-radius: 50%;
          background: #F8FAFC;
          box-shadow: 0 2px 6px rgba(0,0,0,.3);
          transition: transform .16s ease;
        }

        .connector-card__switch[data-on] { background: linear-gradient(135deg,#00D4FF,#8B5CF6); }
        .connector-card__switch[data-on] > span { transform: translateX(1.05rem); }

        .connector-card__hint {
          display: inline-flex;
          align-items: center;
          gap: .3rem;
          color: #64748B;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .connector-card__panel {
          margin-top: .35rem;
          padding-top: .9rem;
          border-top: 1px solid rgba(255,255,255,.08);
        }

        .connector-panel__lede {
          margin: 0 0 .9rem;
          color: #A9B6C9;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.6;
        }

        .connector-panel__checks { display: grid; gap: .5rem; margin: 0; padding: 0; list-style: none; }

        .connector-panel__checks li {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 1rem;
          padding: .55rem .7rem;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: .6rem;
          background: rgba(0,0,0,.16);
        }

        .connector-panel__checks li[data-ok] { border-color: rgba(134,239,172,.24); }

        .connector-panel__checks strong {
          color: #E2E8F5;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .connector-panel__checks span {
          color: #94A3B8;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .connector-panel__note {
          margin: .8rem 0 0;
          color: #FCD34D;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.55;
        }

        @media (prefers-reduced-motion: reduce) {
          .connector-card,
          .connector-card__switch,
          .connector-card__switch > span { transition: none; }
        }
      `}</style>
    </div>
  );
}
